import * as Sentry from '@sentry/nextjs';

import {
  BookingGateWayModeIdEnum,
  convert24HourTo12Hour,
  dateFormatLongMonth,
  getYearsDifferenceExact,
  guestBookingEndpoint,
  numberOfDaysUntil,
} from '@alluradirect/react';
import { Box, Button, Typography } from '@mui/material';
import { CheckoutParamEnum, PaymentTypeEnum } from 'components/views/pages/Checkout/types';
import React, { useState } from 'react';
import {
  amountDueToday,
  generateDatesDisplay,
  generateGuestsDisplay,
  generatePetsDisplay,
} from 'components/views/pages/Checkout/helpers';
import {
  balancePaymentDate,
  balancePaymentTotal,
  hasBalancePayment,
} from 'components/views/pages/Checkout/CheckoutSummary/helpers';

import { AppCheckboxWithLabel } from 'components/core/AppCheckbox/AppCheckboxWithLabel';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { CheckoutBoxWrapper } from '../styles';
import { CheckoutCancellationPolicy } from 'components/views/pages/Checkout/CheckoutStep2/components/CheckoutCancellationPolicy';
import { CheckoutGatewayClosed } from 'components/views/pages/Checkout/components/CheckoutGatewayClosed';
import { CheckoutInstantBooking } from 'components/views/pages/Checkout/components/CheckoutInstantBooking';
import { CheckoutPickNewDatesBackToSearch } from 'components/views/pages/Checkout/CheckoutStep3/components/CheckoutPickNewDatesBackToSearch';
import { CheckoutSectionTitle } from 'components/views/pages/Checkout/components/CheckoutSectionTitle';
import { CheckoutStepProps } from 'pages/[propertyId]/reservenow';
import { CheckoutSummaryPriceDetails } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryPriceDetails';
import { ChevronRight } from '@mui/icons-material';
import ConfirmationDialog from 'components/core/ConfirmationDialog/ConfirmationDialog';
import { Fade } from '@mui/material';
import Grid from 'components/core/Grid';
import { IS_DIRECT_VACATIONS } from 'config/envSetup';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { StripeField } from 'components/core/stripe/StripeField/StripeField';
import dayjs from 'dayjs';
import { formatCanadianCurrency } from 'helpers/currencyHelpers';
import { isInvalidDatesCheckoutError } from 'components/views/pages/Checkout/hooks/useCheckout';
import styled from 'styled-components';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { useAppParam } from 'hooks/useAppParam';
import { useCheckoutParams } from 'components/views/pages/Checkout/hooks/useCheckoutParams';
import { useEnsureLoggedInCheckout } from 'components/views/pages/Checkout/hooks/useEnsureLoggedInCheckout';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import { useStripeField } from 'components/core/stripe/StripeField/hooks/useStripeField';
import { useToggle } from '@alluradirect/react';
import { CheckoutFraudsterWarning } from '../components/CheckoutFraudsterWarning';

export const ProcessingWrapper = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1111111;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
`;

export const ProcessingContent = styled.div`
  width: 320px;
  padding: 32px;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

export const ProcessingText = styled(Typography)`
  overflow: hidden;
  white-space: nowrap;
  width: 0;
  animation: typing 5s infinite;
  text-align: center;
  display: flex;
  justify-content: flex-end;

  @keyframes typing {
    from {
      width: 0%;
      opacity: 0;
    }
    to {
      width: 100%;
      opacity: 1;
    }
  }

  & svg {
    font-size: 30px;
  }
`;

export const ErrorContainer = styled(Box)`
  border-left: 1px solid ${props => props.theme.palette.error.main};
  border-right: 1px solid ${props => props.theme.palette.error.main};
  border-bottom: 1px solid ${props => props.theme.palette.error.main};
  margin-bottom: ${props => props.theme.spacing(3)};
`;

export const ErrorTitle = styled(Typography)`
  background-color: ${props => props.theme.palette.error.main};
  color: #fff;
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
`;

export const ChargeTypography = styled(Typography)`
  text-align: center;
  background-color: ${props => props.theme.palette.success.main};  
  border-radius: 5px;
  color: #fff;
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(1)};
  font-weight: 500;

  &:not(:first-child) {
    border-top: 0px;
    border-top: 1px solid ${props => props.theme.palette.grey[300]};
  }

  &.balance-due {
    background-color: ${props => props.theme.palette.grey[50]};
    color: #333;
    border: 1px solid ${props => props.theme.palette.grey[300]};
  }
`;

export const StyledGrid = styled(Grid)`
  display: flex;

  & svg {
    margin-right: ${props => props.theme.spacing(0.5)};
  }
`;

export const CCInputGrid = styled(Grid)``;

export const AgreeTermsGrid = styled(Grid)`
  & svg {
    font-size: 48px;
  }

  & .MuiTypography-root {
    font-size: 1rem;
  }
`;

export const CheckoutStep3 = (props: CheckoutStepProps) => {
  const router = useRouter();
  const { isMd } = useScreenWidth();

  useEnsureLoggedInCheckout(props.loginState, props.isSessionTimeout);

  const [isInvalidDates, setIsInvalidDates] = useState(false);

  const stepParam = useAppParam(CheckoutParamEnum.step);

  const userFirstName = props.userData?.FIRST_NAME;
  const userLastName = props.userData?.LAST_NAME;
  const initialCardholderName = userFirstName && userLastName ? `${userFirstName} ${userLastName}` : '';

  const {
    handleStripeSubmission,
    onChangeCard,
    onChangeCardholderName,
    hasCardholderNameError,
    cardError,
    stripeApiError,
    alluraServerApiError,
    isLoading,
    cardholderName,
  } = useStripeField({
    initialCardholderName,
  });

  const toggleAgreement = useToggle({ isOpen: false });
  const toggle = useToggle();
  const rentalAgreement = props.checkoutData?.BOOKING?.RENTAL_AGREEMENT ?? '';

  const gatewayMode = props.checkoutData?.BOOKING.PROPERTY.GATEWAY_MODE.ID;
  const isGatewayClosed = gatewayMode === BookingGateWayModeIdEnum.closed;

  const currency_code = props.currency_code;
  const currency_multiplier = props.currency_multiplier;

  const amountCharged = formatCanadianCurrency(amountDueToday(props.checkoutData, props.paymentType), { currency_code, currency_multiplier });
  const amountCharged_CAD = formatCanadianCurrency(amountDueToday(props.checkoutData, props.paymentType));

  const isBalancePayment = hasBalancePayment({ checkoutData: props.checkoutData, paymentType: props.paymentType });
  const balanceTotal = formatCanadianCurrency(balancePaymentTotal(props.checkoutData), { minMaxFractionDigits: 2, currency_code, currency_multiplier })
  const balanceTotal_CAD = formatCanadianCurrency(balancePaymentTotal(props.checkoutData), { minMaxFractionDigits: 2 })

  const balanceDueDate = balancePaymentDate(props.checkoutData);

  const { owner_offer_code, owner_offer_id, allura_offer_code, allura_offer_id, owner_gift_certificate_code, owner_gift_certificate_id, allura_gift_certificate_code, allura_gift_certificate_id, } = useCheckoutParams();

  const handleSubmit = () => {
    setIsInvalidDates(false);
    props.setIsMakingPayment(true);

    handleStripeSubmission({
      endpoint: guestBookingEndpoint,
      additionalSendData: {
        id: props.userId,
        property_id: props.pid,
        arrival: props.startDate,
        departure: props.endDate,
        party_size_adults: props.adults,
        party_size_kids: props.pChildren,
        party_size_infants: props.infants,
        number_of_pets: props.pets,
        is_marketing_emails: props.marketing,
        is_direct_vacations: IS_DIRECT_VACATIONS(),
        notes_for_owner: props.message,
        is_full_payment: Number(props.paymentType) === PaymentTypeEnum.SinglePayment ? 1 : 0,

        // Test flags only for development purposes
        test_flag_dates_taken: false,
        test_flag_server_error: false,
        test_flag_network_failure: false,
        test_flag_no_rates: false,

        owner_offer_code: owner_offer_code ? encodeURIComponent(owner_offer_code) : '',
        owner_offer_id: owner_offer_id ?? '',
        allura_offer_code: allura_offer_code ? encodeURIComponent(allura_offer_code) : '',
        allura_offer_id: allura_offer_id ?? '',
        owner_gift_certificate_code: owner_gift_certificate_code ? encodeURIComponent(owner_gift_certificate_code) : '',
        owner_gift_certificate_id: owner_gift_certificate_id ?? '',
        allura_gift_certificate_code: allura_gift_certificate_code ? encodeURIComponent(allura_gift_certificate_code) : '',
        allura_gift_certificate_id: allura_gift_certificate_id ?? '',
      },
      onSuccess: data => {
        const bid = data?.DATA.BOOKING_ID;

        if (!bid) {
          Sentry.captureException(
            new Error(
              'failed to navigate after submitting on step 3, booking id returned from guest booking endpoint is undefined',
            ),
          );
          toast.error('Failed to Complete Booking, Please Contact Support');
          return;
        }

        toast.success('Created Booking');
        props.handleNextStep({
          paramName: CheckoutParamEnum.bookingId,
          paramValue: bid,
        });
      },
      onStripeError: data => {
        const isInvalidDates = isInvalidDatesCheckoutError(new Error(JSON.stringify(data)));
        setIsInvalidDates(isInvalidDates);
        props.setIsMakingPayment(false);

        const clientMessage = data?.ERRORS?.CLIENT_MESSAGE;
        if (clientMessage && clientMessage.toLowerCase().includes('offer')) {
          router.replace(
            {
              pathname: router.pathname,
              query: {
                ...router.query,
                owner_offer_code: undefined,
                owner_offer_id: undefined,
              },
            },
            undefined,
            {
              shallow: false,
              scroll: false,
            },
          );
        }
      },
      sendWithoutCardData: isGatewayClosed,
    });
  };



  const onClickEditTripDetails = () => {
    stepParam.setParam(1);
  };

  const allowsPets = props.checkoutData?.BOOKING.PROPERTY.IS_PET_FRIENDLY;

  const datesArrivalDay = dayjs(props.startDate).format('dddd');
  const datesDepartureDay = dayjs(props.endDate).format('dddd');

  //I was not sure how to convert the date into a string directly for the convert24HourTo12Hour helper, this can be cleaned up
  const checkInTimeString = `${props.checkoutData?.BOOKING.PROPERTY.RULES.CHECK_IN.toString()}`;
  const checkOutTimeString = `${props.checkoutData?.BOOKING.PROPERTY.RULES.CHECK_OUT.toString()}`;
  const checkInTime = convert24HourTo12Hour(checkInTimeString);
  const checkOutTime = convert24HourTo12Hour(checkOutTimeString);

  const datesDisplay = generateDatesDisplay({ startDate: props.startDate, endDate: props.endDate });

  const guestsDisplay = generateGuestsDisplay({
    adults: props.adults,
    children: props.pChildren,
    infants: props.infants,
  });

  const petsDisplay = generatePetsDisplay({ pets: props.pets, allowsPets });

  const arrival = props.checkoutData ? props.checkoutData?.BOOKING.DATES.ARRIVAL : dayjs();
  const days_to_arrival = numberOfDaysUntil(arrival);
  const is_within_fraudster_warning_range = days_to_arrival <= 7;
  const is_new_guest = props.userData && props.userData.GUEST_PROFILE && props.userData.GUEST_PROFILE.NUM_BOOKINGS == 0;
  const isShowFraudsterWarning = is_new_guest && is_within_fraudster_warning_range;

  const guestDOB = props.checkoutData?.BOOKING.GUEST.DATE_OF_BIRTH;
  const minAgeToBook = props.checkoutData?.BOOKING.PROPERTY.RULES.MIN_AGE_TO_BOOK;

  let buttonText = isGatewayClosed ? 'Request to Book' : 'Confirm and Pay';

  let isMinAgeToBook = true;
  let guestAge = 0;
  if (guestDOB != undefined && guestDOB.length && minAgeToBook != undefined) {
    guestAge = getYearsDifferenceExact(new Date(guestDOB), new Date());
    if (guestAge < minAgeToBook) {
      isMinAgeToBook = false;
      buttonText = `You must be at least ${minAgeToBook} years old to book this property`;
    }
  }

  let isUnderMaxBookableDays = true;
  const maxBookableDays = props.checkoutData?.BOOKING.PROPERTY.RULES.MAX_BOOKABLE_DAYS;
  const bookingNights = props.checkoutData?.BOOKING.NIGHTS;
  if (maxBookableDays != undefined && bookingNights != undefined && bookingNights > maxBookableDays) {
    isUnderMaxBookableDays = false;
    buttonText = `This property can be booked for a maximum of ${maxBookableDays} nights`;
  }
  const is_not_canadian_currency = currency_code != 'CAD';

  return (
    <Fade in>
      <div>
        <Grid container>
          <CheckoutBoxWrapper>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} mb={2}>
                <CheckoutSectionTitle mb={0}>Trip Details</CheckoutSectionTitle>
                <Button size="small" variant="contained" color="secondary" onClick={onClickEditTripDetails}>
                  Edit
                </Button>
              </Box>

              <Grid container spacing={1}>
                <StyledGrid item xs={12}>
                  <ArrowRightIcon />
                  <Typography variant="body1">{datesDisplay}</Typography>
                </StyledGrid>

                <StyledGrid item xs={12} ml={'16px'}>
                  <ArrowRightIcon />
                  <LoadingSkeleton isLoading={!props.checkoutData}>
                    <Typography variant="body1">Check-in {datesArrivalDay} ({checkInTime})</Typography>
                  </LoadingSkeleton>
                </StyledGrid>

                <StyledGrid item xs={12} ml={'16px'}>
                  <ArrowRightIcon />
                  <LoadingSkeleton isLoading={!props.checkoutData}>
                    <Typography variant="body1">Check-out {datesDepartureDay} ({checkOutTime})</Typography>
                  </LoadingSkeleton>
                </StyledGrid>

                <StyledGrid item xs={12}>
                  <ArrowRightIcon />
                  <Typography variant="body1">{guestsDisplay}</Typography>
                </StyledGrid>

                <StyledGrid item xs={12}>
                  <ArrowRightIcon />
                  <LoadingSkeleton isLoading={!props.checkoutData}>
                    <Typography variant="body1">{petsDisplay}</Typography>
                  </LoadingSkeleton>
                </StyledGrid>
              </Grid>
            </Grid>
          </CheckoutBoxWrapper>

          {isMd ? (
            <>
              <Grid item xs={12}>
                <AppDivider marginVertical="16px" borderWidth="0" />
              </Grid>
              <CheckoutBoxWrapper>
                <CheckoutSummaryPriceDetails {...props} hasTopAppDivider={false} hasBottomAppDivider={false} />
              </CheckoutBoxWrapper>
            </>
          ) : null}

          <CheckoutCancellationPolicy checkoutData={props.checkoutData} title="Review Cancellation Policy" />

          <Grid item xs={12}>
            <AppDivider marginVertical={'16px'} borderWidth="0" />
          </Grid>

          {isGatewayClosed ? (
            <CheckoutBoxWrapper>
              <Box>
                <CheckoutGatewayClosed isLoading={!props.checkoutData} />
                <Grid item xs={12} mt={4}>
                  <ChargeTypography variant="body1">
                    The payment is not due until the host approves your request
                  </ChargeTypography>
                </Grid>
              </Box>
            </CheckoutBoxWrapper>
          ) : (
            <>
              <CheckoutBoxWrapper>
                <CheckoutInstantBooking isLoading={!props.checkoutData} />
              </CheckoutBoxWrapper>
              {isShowFraudsterWarning ? (
                <>
                  <Grid item xs={12}>
                    <AppDivider marginVertical={'16px'} borderWidth="0" />
                  </Grid>
                  <CheckoutBoxWrapper>
                    <CheckoutFraudsterWarning isLoading={!props.checkoutData} />
                  </CheckoutBoxWrapper>
                </>
              )
                : null
              }

              <CheckoutBoxWrapper mt={4}>
                <Box sx={{ width: '100%' }}>
                  <Grid item xs={12}>
                    <CheckoutSectionTitle mb={2}>Enter Billing Info</CheckoutSectionTitle>
                  </Grid>
                  <CCInputGrid item xs={12} mb={3}>
                    <Grid container spacing={2}>
                      <StripeField
                        cardholderName={cardholderName}
                        hasCardholderNameError={hasCardholderNameError}
                        cardError={cardError}
                        stripeApiError={stripeApiError}
                        alluraServerApiError={alluraServerApiError}
                        onChangeCard={onChangeCard}
                        onChangeCardholderName={onChangeCardholderName}
                        hidePostalCode={true}
                      />
                    </Grid>
                  </CCInputGrid>

                  {stripeApiError && (
                    <Grid item xs={12}>
                      <ErrorContainer>
                        <ErrorTitle variant="h6">Why was the card declined?</ErrorTitle>
                        <Typography variant="body2">
                          <ul>
                            <li>
                              Most declines are the result of your bank blocking the charge.{' '}
                              <strong>Call the phone number on the back of your credit card</strong>, and ask them to
                              allow the charge. Retry the payment.
                            </li>
                            <li>The credit card has expired.</li>
                            <li>Insufficent funds. Check that there are funds available on the credit card.</li>
                            <li>The CVV/CVC code was entered incorrectly.</li>
                          </ul>
                        </Typography>
                      </ErrorContainer>
                    </Grid>
                  )}
                  {isInvalidDates ? (
                    <CheckoutPickNewDatesBackToSearch
                      startDate={props.startDate}
                      endDate={props.endDate}
                      checkoutData={props.checkoutData}
                    />
                  ) : null}
                  {/* <CheckoutApplyPromotion /> */}

                  <Grid item xs={12}>

                    <ChargeTypography variant="body1">
                      <strong>Charge today is {amountCharged_CAD} CAD<br /></strong>
                      {is_not_canadian_currency ?
                        <Typography variant="caption">
                          {amountCharged} {currency_code} (current exchange rate)
                        </Typography>
                        : null}
                    </ChargeTypography>

                    {isBalancePayment ? (
                      <ChargeTypography variant="body1" className="balance-due" mt={1}>
                        <strong>Remainder due by {dateFormatLongMonth(dayjs(balanceDueDate))} is {balanceTotal_CAD} CAD</strong><br />
                        {is_not_canadian_currency ?
                          <Typography variant="caption">
                            {balanceTotal} {currency_code} (current exchange rate)
                          </Typography>
                          : null}
                      </ChargeTypography>
                    ) : null}
                  </Grid>
                </Box>
              </CheckoutBoxWrapper>
            </>
          )}
          <CheckoutBoxWrapper mt={4}>
            <Box>
              <Grid item xs={12}>
                <CheckoutSectionTitle>Confirm and Reserve</CheckoutSectionTitle>
              </Grid>
              <AgreeTermsGrid item xs={12} mb={4}>
                <AppCheckboxWithLabel
                  checked={toggleAgreement.isOpen}
                  onChange={toggleAgreement.toggle}
                  label="I understand and agree to the rental agreement and will abide by all rules and policies as listed in the contract."
                  labelPlacement={'end'}
                />
                <Button onClick={toggle.open} variant="text" fullWidth size="small">
                  View Rental Agreement
                </Button>
              </AgreeTermsGrid>

              <LoadingButton
                text={buttonText}
                isFullWidth
                size="large"
                isDisabled={!toggleAgreement.isOpen || !isMinAgeToBook || !isUnderMaxBookableDays}
                isLoading={isLoading}
                onClick={handleSubmit}
                endIcon={isMinAgeToBook && isUnderMaxBookableDays ? <ChevronRight /> : null}
              ></LoadingButton>
            </Box>
          </CheckoutBoxWrapper>

          <Grid item xs={12}>
            {isLoading && (
              <ProcessingWrapper>
                <ProcessingContent>
                  <Typography variant="h6" fontWeight="500">
                    {!isGatewayClosed ? 'Processing Your Payment' : 'Confirming Your Request'}
                  </Typography>
                  <ProcessingText variant="body1" fontWeight="500" my={2}>
                    <MoreHorizIcon color="secondary" />
                    <MoreHorizIcon color="secondary" />
                    <MoreHorizIcon color="secondary" />
                    <MoreHorizIcon color="secondary" />
                    <MoreHorizIcon color="secondary" />
                    <MoreHorizIcon color="secondary" />
                    <MoreHorizIcon color="secondary" />
                  </ProcessingText>

                  <Typography variant="body2" fontWeight="500">
                    {!isGatewayClosed
                      ? 'Payments may take over 3 minutes to complete, please do not leave this page during this process.'
                      : 'This process may take over 3 minutes to complete, please do not leave this page during this time.'}
                  </Typography>
                </ProcessingContent>
              </ProcessingWrapper>
            )}
          </Grid>
        </Grid>
        <ConfirmationDialog
          dialogTitle={'Rental Agreement'}
          isDialogOpen={toggle.isOpen}
          handleCloseDialog={toggle.close}
          hasBottomBar={false}
        >
          <div dangerouslySetInnerHTML={{ __html: rentalAgreement }}></div>
        </ConfirmationDialog>
      </div >
    </Fade >
  );
};
